function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import { isEmpty } from '@ember/utils';
import { assert } from '@ember/debug';
import { MEASUREMENT_UNIT_MAX_PRECISION } from '@square/dashboard-data/models/catalog/catalog-service-constants';
import Fraction from 'fraction.js';
const DECIMAL_PARTS = 100000;
export const UNSPECIFIED_PRECISION = -1;

// This object is only intended for representing decimal stock counts used by the inventory2 service.
// The implementation assumes that inputs will follow the constraints of fractional quantities in inventory2,
// which include a limit of 10 integer digits and 5 decimal digits. Other inputs have undefined behavior.
export default class DecimalStockCount {
  constructor(args) {
    _defineProperty(this, "_integer", 0);
    // should always be non-negative
    _defineProperty(this, "_decimal", 0);
    // should always be non-negative
    _defineProperty(this, "_isNegative", false);
    const {
      _integer = 0,
      _decimal = 0,
      _isNegative = false
    } = args || {};
    this._integer = _integer;
    this._decimal = _decimal;
    this._isNegative = _isNegative;
  }
  static fromString(decimalQuantityString) {
    if (isEmpty(decimalQuantityString)) {
      return null;
    }
    let _isNegative = false;
    if (decimalQuantityString[0] === '-') {
      _isNegative = true;
      decimalQuantityString = decimalQuantityString.slice(1);
    }
    const parts = decimalQuantityString.split('.');
    const _integer = Number(parts[0]);
    let _decimal = 0;
    if (parts[1]) {
      assert('decimal should not be longer than 5 digits', parts[1].length <= 5);
      _decimal = Number(parts[1].padEnd(5, '0'));
    }
    if (_integer === 0 && _decimal === 0) {
      _isNegative = false;
    }
    return new DecimalStockCount({
      _isNegative,
      _integer,
      _decimal
    });
  }
  static zero() {
    return new DecimalStockCount();
  }
  static serialize(value) {
    if (isEmpty(value)) {
      return null;
    }
    return value.toString();
  }
  static deserialize(valueString) {
    return this.fromString(valueString);
  }
  add(otherQuantity) {
    if (!otherQuantity) {
      return this.copy();
    }
    const aInt = this._isNegative ? -this._integer : this._integer;
    const aDecimal = this._isNegative ? -this._decimal : this._decimal;
    const bInt = otherQuantity._isNegative ? -otherQuantity._integer : otherQuantity._integer;
    const bDecimal = otherQuantity._isNegative ? -otherQuantity._decimal : otherQuantity._decimal;
    let newInteger = aInt + bInt;
    let newDecimal = aDecimal + bDecimal;
    if (newDecimal <= -DECIMAL_PARTS) {
      newDecimal += DECIMAL_PARTS;
      newInteger--;
    } else if (newDecimal >= DECIMAL_PARTS) {
      newDecimal -= DECIMAL_PARTS;
      newInteger++;
    }
    let newIsNegative;
    if (newInteger === 0) {
      newIsNegative = newDecimal < 0;
      newDecimal = Math.abs(newDecimal);
    } else if (newInteger > 0) {
      newIsNegative = false;
      if (newDecimal < 0) {
        newDecimal += DECIMAL_PARTS;
        newInteger--;
      }
    } else {
      newIsNegative = true;
      if (newDecimal > 0) {
        newDecimal -= DECIMAL_PARTS;
        newInteger++;
      }
      newInteger = Math.abs(newInteger);
      newDecimal = Math.abs(newDecimal);
    }
    return new DecimalStockCount({
      _integer: newInteger,
      _decimal: newDecimal,
      _isNegative: newIsNegative
    });
  }
  subtract(otherQuantity) {
    if (!otherQuantity) {
      return this.copy();
    }
    return this.add(otherQuantity.negate());
  }
  multiply(otherQuantity) {
    const thisDecimal = new Fraction(this.toString());
    const otherDecimal = new Fraction(otherQuantity.toString());
    return DecimalStockCount.fromString(thisDecimal.mul(otherDecimal).valueOf().toFixed(5));
  }
  divide(otherQuantity) {
    const thisDecimal = new Fraction(this.toString());
    const otherDecimal = new Fraction(otherQuantity.toString());
    return DecimalStockCount.fromString(thisDecimal.div(otherDecimal).valueOf().toFixed(5));
  }
  negate() {
    if (this._integer === 0 && this._decimal === 0) {
      return this.copy();
    }
    return new DecimalStockCount({
      _integer: this._integer,
      _decimal: this._decimal,
      _isNegative: !this._isNegative
    });
  }
  abs() {
    return new DecimalStockCount({
      _integer: this._integer,
      _decimal: this._decimal,
      _isNegative: false
    });
  }
  equals(otherQuantity) {
    if (!otherQuantity) {
      return false;
    }
    return this._integer === otherQuantity._integer && this._decimal === otherQuantity._decimal && this._isNegative === otherQuantity._isNegative;
  }
  lt(otherQuantity) {
    if (!otherQuantity) {
      otherQuantity = DecimalStockCount.zero();
    }
    if (this._isNegative !== otherQuantity._isNegative) {
      return this._isNegative;
    }
    if (this._isNegative) {
      if (this._integer !== otherQuantity._integer) {
        return this._integer > otherQuantity._integer;
      } else {
        return this._decimal > otherQuantity._decimal;
      }
    } else {
      if (this._integer !== otherQuantity._integer) {
        return this._integer < otherQuantity._integer;
      } else {
        return this._decimal < otherQuantity._decimal;
      }
    }
  }
  lte(otherQuantity) {
    return this.lt(otherQuantity) || this.equals(otherQuantity);
  }
  gt(otherQuantity) {
    return !this.lt(otherQuantity) && !this.equals(otherQuantity);
  }
  isPositive() {
    return !this._isNegative && !this.isZero();
  }
  isNegative() {
    return this._isNegative && !this.isZero();
  }
  isZero() {
    return this._integer === 0 && this._decimal === 0;
  }
  effectivePrecision() {
    let precision = 5;
    let decimal = this._decimal;
    while (precision > 0 && decimal % 10 === 0) {
      precision--;
      decimal = decimal / 10;
    }
    return precision;
  }
  toInt() {
    let intValue = this._integer;
    if (this.isNegative()) {
      intValue *= -1;
    }
    return intValue;
  }
  toString() {
    return `${this._isNegative ? '-' : ''}${this._integer}.${this._decimal.toString().padStart(5, '0')}`;
  }
  toStringNoTrailingZero(formatter) {
    return this.formatted({
      formatter
    });
  }
  formatted({
    minPrecision = 0,
    maxPrecision = MEASUREMENT_UNIT_MAX_PRECISION,
    formatter
  }) {
    return formatter.buildNumberFormatter().setMaximumFractionDigits(maxPrecision).setMinimumFractionDigits(minPrecision).format(this.toString());
  }
  copy() {
    return new DecimalStockCount({
      _integer: this._integer,
      _decimal: this._decimal,
      _isNegative: this._isNegative
    });
  }
}